import { useEffect, useRef, useState } from "react";
import { Calendar, Check, ChevronDown, ChevronsRight, Clock, Lock } from "lucide-react";
import { USERTYPE } from "app/utils/types";
import { getObjectFromLocalStore } from "app/utils/localStore";

export type YearStepStatus = "COMPLETED" | "SKIPPED" | "CURRENT" | "LOCKED";

export interface YearStep {
  yearNumber: number;
  calendarYear?: number;
  status: YearStepStatus;
  label?: string;
}

interface YearStepperProps {
  steps: YearStep[];
  onYearClick?: (step: YearStep) => void;
  mode?: "overlay" | "inline" | "navbar";
  phases?: string[];
  selectedPhase?: string;
  onPhaseClick?: (phase: string) => void;
  selectedYear?: any;
}

const StepIcon = ({ status }: { status: YearStepStatus }) => {
  switch (status) {
    case "COMPLETED":
      return (
        <div className="w-[42px] h-[42px] rounded-full bg-[#22C55E] flex items-center justify-center shadow-sm">
          <Check size={18} strokeWidth={3} color="#fff" />
        </div>
      );
    case "SKIPPED":
      return (
        <div className="w-[42px] h-[42px] rounded-full bg-[#E5E7EB] border border-[#D1D5DB] flex items-center justify-center">
          <ChevronsRight size={18} strokeWidth={2} color="#9CA3AF" />
        </div>
      );
    case "CURRENT":
      return (
        <div className="w-[42px] h-[42px] rounded-full bg-[#1E3A5F] flex items-center justify-center shadow-sm">
          <Clock size={17} strokeWidth={2} color="#fff" />
        </div>
      );
    case "LOCKED":
      return (
        <div className="w-[42px] h-[42px] rounded-full bg-[#E5E7EB] border border-[#D1D5DB] flex items-center justify-center">
          <Lock size={15} strokeWidth={2} color="#9CA3AF" />
        </div>
      );
    default:
      return null;
  }
};

/** Small colored dot used inside the navbar year dropdown */
const StatusDot = ({ status }: { status: YearStepStatus }) => {
  const base = "flex items-center justify-center w-4 h-4 rounded-full flex-shrink-0";
  switch (status) {
    case "COMPLETED":
      return (
        <span className={`${base} bg-[#22C55E]`}>
          <Check size={8} strokeWidth={3} color="#fff" />
        </span>
      );
    case "SKIPPED":
      return (
        <span className={`${base} bg-[#E5E7EB]`}>
          <ChevronsRight size={8} strokeWidth={2} color="#9CA3AF" />
        </span>
      );
    case "CURRENT":
      return (
        <span className={`${base} bg-[#1E3A5F]`}>
          <Clock size={8} strokeWidth={2} color="#fff" />
        </span>
      );
    case "LOCKED":
      return (
        <span className={`${base} bg-[#E5E7EB]`}>
          <Lock size={7} strokeWidth={2} color="#9CA3AF" />
        </span>
      );
    default:
      return null;
  }
};

const CONNECTOR_MT = "mt-[34px]";

const YearStepper = ({
  steps,
  onYearClick,
  mode = "overlay",
  phases,
  selectedYear,
  selectedPhase,
  onPhaseClick,
}: YearStepperProps) => {
  const [showCalendarYear, setShowCalendarYear] = useState(false);
  const [yearDropdownOpen, setYearDropdownOpen] = useState(false);
  const [phaseDropdownOpen, setPhaseDropdownOpen] = useState(false);
  const [selectedInlineYear, setSelectedInlineYear] = useState<number | null>(null);
  const [localSelectedStep, setLocalSelectedStep] = useState<YearStep | null>(null);
  const [localSelectedPhase, setLocalSelectedPhase] = useState<string | null>(null);
  const navbarRef = useRef<HTMLDivElement>(null);
  const lastClickRef = useRef<{ yearNumber: number; time: number } | null>(null);
  const userAccess = getObjectFromLocalStore("useraccess");

  useEffect(() => {
    if (mode !== "navbar") return;
    const handleClickOutside = (e: MouseEvent) => {
      if (navbarRef.current && !navbarRef.current.contains(e.target as Node)) {
        setYearDropdownOpen(false);
        setPhaseDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    // eslint-disable-next-line
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [mode]);

  const currentStep = steps?.find((s) => s.status === "CURRENT") ?? selectedYear ?? steps?.[0];
  const activeStep = (localSelectedStep ?? currentStep) as YearStep | undefined;
  const activePhase = localSelectedPhase ?? selectedPhase ?? phases?.[0];

  const getYearLabel = (step: YearStep) =>
    showCalendarYear && step.calendarYear != null
      ? `${step.calendarYear}`
      : `YR ${step.yearNumber}`;

  const calendarToggle = (
    <button
      type="button"
      className={`flex items-center justify-center rounded-full border transition-colors flex-shrink-0 absolute left-[8px] top-[8px] ${
        showCalendarYear
          ? "bg-[#1E3A5F] border-[#1E3A5F]"
          : "bg-white border-[#D1D5DB] hover:border-[#9CA3AF]"
      }`}
      onClick={() => setShowCalendarYear((v) => !v)}
      title={showCalendarYear ? "Switch to Plan Year" : "Switch to Calendar Year"}
    >
      <Calendar size={24} color={showCalendarYear ? "#fff" : "#9CA3AF"} />
    </button>
  );
  const phaseLabel = (phase: string): any => {
    console.log(phase, "fdfsdfsd");
    if (phase === "postCredit") return <span className="font-bold conthrax-font">Post-Credit</span>;
    return <span className="font-bold conthrax-font">Pre-Credit</span>;
  };

  if (mode === "navbar") {
    const hasSteps = steps?.length > 0;

    const phaseDropdown = phases && phases.length > 0 && (
      <>
        <div className="w-px h-4 bg-[#E5E7EB]" />
        <div className="relative whitespace-nowrap">
          <button
            type="button"
            className="flex items-center gap-[5px] text-[13px] font-medium text-[#374151] hover:opacity-80 transition-opacity"
            onClick={() => {
              setPhaseDropdownOpen((v) => !v);
              setYearDropdownOpen(false);
            }}
          >
            {phaseLabel(activePhase ?? phases[0])}
            <ChevronDown
              size={13}
              strokeWidth={2}
              color="#6B7280"
              style={{
                transition: "transform 0.15s",
                transform: phaseDropdownOpen ? "rotate(180deg)" : "rotate(0deg)",
              }}
            />
          </button>

          {phaseDropdownOpen && (
            <div className="absolute top-full left-0 mt-2 bg-white rounded-lg shadow-lg border border-[#E5E7EB] z-50 min-w-[150px] py-1 overflow-hidden whitespace-normal">
              {phases?.map((phase) => (
                <button
                  key={phase}
                  type="button"
                  className={`w-full text-left px-3 py-[7px] text-[12px] transition-colors ${
                    phase === (activePhase ?? phases[0])
                      ? "bg-[#EFF6FF] text-[#1E3A5F] font-semibold"
                      : "text-[#374151] hover:bg-[#F9FAFB]"
                  }`}
                  onClick={() => {
                    setLocalSelectedPhase(phase);
                    onPhaseClick?.(phase);
                    setPhaseDropdownOpen(false);
                  }}
                >
                  {phaseLabel(phase)}
                </button>
              ))}
            </div>
          )}
        </div>
      </>
    );

    return (
      <div
        ref={navbarRef}
        className=" bg-white rounded-full px-3 py-[5px] shadow-sm border border-[#E5E7EB]"
      >
        <div className="flex items-center gap-3">
          {hasSteps && currentStep ? (
            <div className="flex flex-col">
              <span className="text-[10px] text-[#6C757D] conthrax-font">Review</span>
              <div className="relative whitespace-nowrap">
                <button
                  type="button"
                  className="flex items-center gap-[5px] text-[13px] font-semibold text-[#1E3A5F] conthrax-font hover:opacity-80 transition-opacity"
                  onClick={() => {
                    setYearDropdownOpen((v) => !v);
                    setPhaseDropdownOpen(false);
                  }}
                >
                  {getYearLabel(activeStep ?? steps?.[0])}
                  <ChevronDown
                    size={13}
                    strokeWidth={2.5}
                    color="#1E3A5F"
                    style={{
                      transition: "transform 0.15s",
                      transform: yearDropdownOpen ? "rotate(180deg)" : "rotate(0deg)",
                    }}
                  />
                </button>

                {yearDropdownOpen && (
                  <div className="absolute top-full left-0 mt-2 bg-white rounded-lg shadow-lg border border-[#E5E7EB] z-50 min-w-[130px] py-1 overflow-hidden">
                    {steps.map((step) => {
                      const isLocked = step.status === "LOCKED";
                      const isCurr = step.yearNumber === activeStep?.yearNumber;
                      return (
                        <button
                          key={step.yearNumber}
                          type="button"
                          className={`w-full text-left px-3 py-[7px] text-[12px] flex items-center gap-2 transition-colors ${
                            isCurr
                              ? "bg-[#EFF6FF] text-[#1E3A5F] font-bold"
                              : isLocked
                              ? "text-[#9CA3AF] cursor-not-allowed"
                              : "text-[#374151] hover:bg-[#F9FAFB] cursor-pointer"
                          }`}
                          onClick={() => {
                            if (!isLocked) {
                              setLocalSelectedStep(step);
                              onYearClick?.(step);
                              setYearDropdownOpen(false);
                            }
                          }}
                          disabled={isLocked}
                        >
                          <StatusDot status={step.status} />
                          <span className="conthrax-font">{getYearLabel(step)}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <span className="text-[13px] text-[#9CA3AF] conthrax-font">—</span>
          )}
          {phaseDropdown}
        </div>
      </div>
    );
  }

  if (mode === "inline") {
    if (!steps?.length) return null;

    const activeYear =
      selectedInlineYear ??
      steps.find((s) => s.status === "CURRENT")?.yearNumber ??
      steps[0]?.yearNumber;

    const statusColor = (s: YearStepStatus) =>
      s === "COMPLETED" ? "#22C55E" : s === "CURRENT" ? "#1E3A5F" : "#9CA3AF";

    return (
      <div className="w-full bg-white dark:bg-[#2A2A3D] rounded-xl shadow-sm border border-[#E5E7EB] dark:border-[#3A3A50] flex items-stretch overflow-x-auto">
        {/* Calendar toggle */}
        <div className="flex items-center px-3 border-r border-[#E5E7EB] dark:border-[#3A3A50] flex-shrink-0">
          <button
            type="button"
            className={`flex items-center justify-center w-[26px] h-[26px] rounded-full border transition-colors ${
              showCalendarYear
                ? "bg-[#1E3A5F] border-[#1E3A5F]"
                : "bg-white dark:bg-[#2A2A3D] border-[#D1D5DB] hover:border-[#9CA3AF]"
            }`}
            onClick={() => setShowCalendarYear((v) => !v)}
            title={showCalendarYear ? "Switch to Plan Year" : "Switch to Calendar Year"}
          >
            <Calendar size={13} color={showCalendarYear ? "#fff" : "#9CA3AF"} />
          </button>
        </div>

        {/* Tabs */}
        {steps.map((step) => {
          const isActive = step.yearNumber === activeYear;
          const isClickable = step.status !== "LOCKED";
          const color = statusColor(step.status);

          return (
            <button
              key={step.yearNumber}
              type="button"
              disabled={!isClickable}
              onClick={() => {
                if (!isClickable) return;
                setSelectedInlineYear(step.yearNumber);
                onYearClick?.(step);
              }}
              className={`relative flex flex-col items-center justify-center px-5 py-2.5 min-w-[80px] border-b-2 transition-all flex-shrink-0 ${
                isActive ? "border-[#1581ED]" : "border-transparent hover:border-[#CBD5E1]"
              } ${!isClickable ? "cursor-not-allowed opacity-40" : "cursor-pointer"}`}
            >
              <span
                className={`text-[11px] font-bold conthrax-font leading-none ${
                  isActive ? "text-[#1E3A5F] dark:text-[#93C5FD]" : "text-[#6B7280]"
                }`}
              >
                {getYearLabel(step)}
              </span>

              <div className="flex items-center gap-1 mt-1.5">
                <StatusDot status={step.status} />
                {step.label && (
                  <span className="text-[10px] font-medium whitespace-nowrap" style={{ color }}>
                    {step.label}
                  </span>
                )}
              </div>
            </button>
          );
        })}
      </div>
    );
  }

  if (!steps?.length) return null;
  const cardClass = `fixed top-[10%] !z-50 translate-[10px] right-[30px] bg-white px-[24px] pt-[28px] pb-[20px] ${
    userAccess?.userType !== USERTYPE.ADMIN ? "w-[53%]" : "w-[55%]"
  } rounded-xl shadow-md shadow-b-2 flex flex-col gap-[6px] overflow-auto`;
  return (
    <div className={cardClass}>
      <div className="flex justify-start">{calendarToggle}</div>

      <div className="flex items-center justify-between gap-1">
        {steps.map((step, index) => {
          const isClickable = step.status !== "LOCKED";
          const isCurrent = localSelectedStep
            ? step.yearNumber === localSelectedStep.yearNumber
            : step.status === "CURRENT";

          return [
            <div
              key={`step-${step.yearNumber}`}
              className={`flex flex-col items-center gap-[3px] w-[56px] ${
                isClickable ? "cursor-pointer" : "cursor-not-allowed"
              }`}
              onClick={() => {
                if (!isClickable) return;
                const now = Date.now();
                const prev = lastClickRef.current;
                if (prev?.yearNumber === step.yearNumber && now - prev.time < 400) {
                  lastClickRef.current = null;
                  if (phases?.length && onPhaseClick) {
                    const cur = activePhase ?? phases[0];
                    const next = cur === "preCredit" ? "postCredit" : "preCredit";
                    setLocalSelectedPhase(next);
                    onPhaseClick(next);
                  }
                } else {
                  lastClickRef.current = { yearNumber: step.yearNumber, time: now };
                  setLocalSelectedStep(step);
                  onYearClick?.(step);
                }
              }}
            >
              <span
                className={`text-[11px] leading-none font-semibold conthrax-font ${
                  isCurrent ? "text-[#1E3A5F]" : "text-[#6B7280]"
                }`}
              >
                {getYearLabel(step)}
              </span>

              <StepIcon status={step.status} />

              <div className="flex flex-col items-center">
                {step.label ? (
                  <>
                    <span
                      className={`text-[12px] font-medium whitespace-nowrap px-1 py-[2px] rounded-full ${
                        isCurrent
                          ? "text-[#1E3A5F] font-bold"
                          : step.status === "COMPLETED"
                          ? "bg-[#22C55E] text-white"
                          : "bg-[#E5E7EB] text-[#6B7280]"
                      }`}
                    >
                      {step.label}
                    </span>
                    {isCurrent && (
                      <div className="w-full h-[2px] bg-[#1E3A5F] rounded-full mt-[2px]" />
                    )}
                  </>
                ) : (
                  <span className="h-[18px]" />
                )}
              </div>
            </div>,

            index < steps.length - 1 ? (
              <div
                key={`connector-${step.yearNumber}`}
                className={`${CONNECTOR_MT} self-start flex-shrink-0 w-1 h-[2px] bg-[#D1D5DB]`}
              />
            ) : null,
          ];
        })}
      </div>
    </div>
  );
};

export default YearStepper;
